import axios from "axios";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import DataTable from "../components/DataTable";

const ListarMatriculas = ({ id }) => {
  const [matriculas, setMatriculas] = useState([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  // ✅ Helper: convierte el contenido de una celda en "clickeable" (ocupa TODO el td)
  const celdaClickeable = (contenido, matriculaId, title = "Abrir matrícula") => (
    <button
      type="button"
      className="btn p-0 w-100 h-100 text-start border-0 bg-transparent"
      style={{
        cursor: "pointer",
        lineHeight: 1.2,
      }}
      onClick={() => navigate(`/matricula/listarmatriculados/${matriculaId}`)}
      title={title}
    >
      {contenido}
    </button>
  );

  // 🗑️ Eliminar matrícula
  const deleteMatricula = useCallback(async (matriculaId) => {
    try {
      const result = await Swal.fire({
        title: "¿Está seguro?",
        text: "¡No podrá revertir este proceso!",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Sí, eliminar",
        cancelButtonText: "Cancelar",
      });

      if (result.isConfirmed) {
        const { data } = await axios.delete(`/api/matriculas/${matriculaId}`);

        Swal.fire({
          icon: "success",
          title: data?.message || "Matrícula eliminada",
          showConfirmButton: false,
          timer: 1500,
        });
      }
    } catch (error) {
      console.error("Error al eliminar la matrícula:", error.message);
      Swal.fire("Error", "No se pudo eliminar la matrícula.", "error");
    }
  }, []);

  // 📥 Obtener datos
  const getData = useCallback(async () => {
    try {
      setLoading(true);

      const { data } = await axios.get("/api/matriculas/listall/");
      const lista = data?.matriculas || [];

      const matriculasConFunciones = lista.map((matricula) => {
        const nombreProgramaRaw =
          matricula.grupoObjectId?.programaObjectId?.nombrePrograma ||
          "Sin programa";

        // ✅ contenido con truncado para programa
        const programaCell = (
          <div
            className="text-truncate"
            style={{ maxWidth: 380 }}
            title={nombreProgramaRaw}
          >
            {nombreProgramaRaw}
          </div>
        );

        return {
          ...matricula,

          // ✅ TODAS estas columnas quedarán clickeables (como si fuera toda la fila)
          anioLectivo: celdaClickeable(matricula.anioLectivo ?? "—", matricula._id),
          fichaGrupo: celdaClickeable(
            matricula.grupoObjectId?.fichaGrupo || "Sin asignar",
            matricula._id,
            "Ver matriculados"
          ),
          nombrePrograma: celdaClickeable(programaCell, matricula._id),

          cantCuposGrupo: celdaClickeable(
            matricula.grupoObjectId?.cantCuposGrupo ?? "N/A",
            matricula._id
          ),

          detallesCount: celdaClickeable(matricula.detallesCount ?? 0, matricula._id),
          cantidadDesertados: celdaClickeable(matricula.cantidadDesertados ?? 0, matricula._id),
          cantidadTraslados: celdaClickeable(matricula.cantidadTraslados ?? 0, matricula._id),

          // ⚙️ Acciones (NO clickeables para navegar)
          funciones: (
            <div className="d-flex gap-2 flex-wrap justify-content-end">
              <button
                className="btn btn-dark btn-sm d-inline-flex align-items-center justify-content-center"
                style={{ width: 34, height: 34 }}
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(`/etapaproductiva/seguimiento/registromasivo/${matricula._id}`);
                }}
                title="Registrar Seguimientos Masivos"
                type="button"
              >
                <i className="fa-solid fa-file-pen"></i>
              </button>

              <button
                className="btn btn-outline-dark btn-sm d-inline-flex align-items-center justify-content-center"
                style={{ width: 34, height: 34 }}
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(`/etapaproductiva/bitacoras/registromasivo/${matricula._id}`);
                }}
                title="Registrar Bitácoras Masivas"
                type="button"
              >
                <i className="fa-solid fa-book"></i>
              </button>

              <button
                className="btn btn-secondary btn-sm d-inline-flex align-items-center justify-content-center"
                style={{ width: 34, height: 34 }}
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(`/etapaproductiva/registromasivo/${matricula._id}`);
                }}
                title="Registrar Etapa Productiva Masiva"
                type="button"
              >
                <i className="fa-solid fa-briefcase"></i>
              </button>

              <button
                className="btn btn-outline-secondary btn-sm d-inline-flex align-items-center justify-content-center"
                style={{ width: 34, height: 34 }}
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(`/actividad/asistencia/${matricula._id}`);
                }}
                title="Registrar Asistencia"
                type="button"
              >
                <i className="fa-solid fa-clipboard-check"></i>
              </button>

              {/* 
              <button
                className="btn btn-outline-primary btn-sm d-inline-flex align-items-center justify-content-center"
                style={{ width: 34, height: 34 }}
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(`/matricula/listarmatriculados/${matricula._id}`);
                }}
                title="Ver Matriculados"
                type="button"
              >
                <i className="fa-solid fa-users"></i>
              </button>
              */}

              <button
                className="btn btn-warning btn-sm d-inline-flex align-items-center justify-content-center"
                style={{ width: 34, height: 34 }}
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(`/matricula/editar/${matricula._id}`);
                }}
                title="Editar Matrícula"
                type="button"
              >
                <i className="fa-solid fa-pen-to-square"></i>
              </button>

              <button
                className="btn btn-danger btn-sm d-inline-flex align-items-center justify-content-center"
                style={{ width: 34, height: 34 }}
                onClick={async (e) => {
                  e.stopPropagation();
                  await deleteMatricula(matricula._id);
                  getData();
                }}
                title="Eliminar Matrícula"
                type="button"
              >
                <i className="fa-solid fa-trash"></i>
              </button>
            </div>
          ),
        };
      });

      setMatriculas(matriculasConFunciones);
    } catch (error) {
      console.error("Error al cargar las matrículas:", error.message);
      Swal.fire("Error", "No se pudo cargar la lista de matrículas.", "error");
      setMatriculas([]);
    } finally {
      setLoading(false);
    }
  }, [navigate, deleteMatricula]);

  useEffect(() => {
    getData();
  }, [getData]);

  // 📊 Columnas
  const columns = useMemo(
    () => [
      { Header: "Año", accessor: "anioLectivo" },
      { Header: "Grupo", accessor: "fichaGrupo" },
      { Header: "Programa", accessor: "nombrePrograma" },
      { Header: "Solicitados", accessor: "cantCuposGrupo" },
      { Header: "Matriculados", accessor: "detallesCount" },
      { Header: "Deserciones", accessor: "cantidadDesertados" },
      { Header: "Traslados", accessor: "cantidadTraslados" },
      { accessor: "funciones" },
    ],
    []
  );

  return (
    <div className="container-fluid px-2 px-md-4">
      <div className="bg-white border rounded-4 shadow-sm overflow-hidden">
        {/* Header */}
        <div className="px-3 px-md-4 py-3 border-bottom bg-white">
          <div className="d-flex justify-content-between align-items-start flex-wrap gap-3">
            <div>
              <h5 className="mt-2 mb-1 fw-semibold">Matrículas</h5>
              <small className="text-secondary">
                Gestión de matrículas, cupos y acciones masivas.
              </small>
            </div>

            <div className="d-flex gap-2 flex-wrap">
              <button
                className="btn btn-outline-secondary"
                type="button"
                onClick={getData}
                disabled={loading}
                title="Actualizar"
              >
                <i
                  className={`fa-solid ${loading ? "fa-spinner fa-spin" : "fa-rotate"
                    } me-2`}
                ></i>
                Actualizar
              </button>

              <button
                className="btn btn-dark"
                type="button"
                onClick={() => navigate("/matricula/registrar")}
                title="Crear Matrícula"
              >
                <i className="fa-solid fa-plus me-2"></i>
                Nuevo
              </button>
            </div>
          </div>
        </div>

        {/* Body */}
        <div>
          {loading ? (
            <div className="text-center text-secondary py-5">
              <div className="spinner-border" role="status" />
              <div className="mt-3">Cargando matrículas…</div>
            </div>
          ) : Array.isArray(matriculas) && matriculas.length > 0 ? (
            <div className="overflow-hidden">
              <DataTable columns={columns} data={matriculas} />
            </div>
          ) : (
            <div className="text-center text-secondary py-5">
              <div className="display-6 mb-2">🧾</div>
              <div className="fw-semibold">No hay matrículas registradas</div>
              <div className="small mb-3">
                Crea la primera matrícula para iniciar la gestión.
              </div>
              <button
                className="btn btn-dark"
                type="button"
                onClick={() => navigate("/matricula/registrar")}
              >
                <i className="fa-solid fa-plus me-2"></i>
                Crear matrícula
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ListarMatriculas;